import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import * as am5 from '@amcharts/amcharts5'
import * as am5xy from '@amcharts/amcharts5/xy'
import { Box, Button, ButtonBase, Skeleton, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { IconSeeker } from '@ui-seeker/core'

import { AppAntSwitch, AppBrandMark, AppEmptyLine, AppNote, secondaryButtonSx } from '@components'
import { BI_ROUTES } from '@constants'
import { dsColorNew } from '@palette'
import { axisFloor, compareMarker, curveFoot, curveSubtitle, curveTooltip, labelColumnWidth, labelLayout, nextCursorDay, noCurveText, notCapturedZone } from '../@services/curve'
import { money } from '../@services/format'


const HEIGHT = 230
const box = { border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '12px', p: '12px 14px 10px', mb: 2, backgroundColor: dsColorNew.white }

/**
 * The amCharts line chart of the curve (UX v4, plan § 10.2): x = days before pick-up, inverted (`days` → pick-up), the
 * corporate chart theme (`Theme/charts.js`, installed on every root), no scrollbars (R10). Drawn with amCharts itself
 * rather than `AmchartSeeker`: the curve needs an inverted value axis, dotted and hollow-point series, a shaded range,
 * a vertical mark and the cursor moved from the keyboard, and the wrapper exposes none of them.
 */
export function CurveChart({
	curve, hidden, showPrev, showOthers, code, marker, zone, hover, onHover, label,
	// "Price curve of a date" (CU-869fmjzrg): taller, points on every line, one line highlighted, a label at the end of each
	// line, click (or Enter) on a day, a ring on the move that came first. The cell's curve passes none of them.
	height = HEIGHT, allBullets = false, highlight = null, endLabels = false, onDayClick = null, ring = null,
}) {
	const el = useRef(null)
	const api = useRef(null)
	const hoverRef = useRef(hover)
	hoverRef.current = hover
	const [ends, setEnds] = useState(null)
	const drawnLines = endLabels ? [...curve.main.filter(s => !hidden[s.key]), ...(showOthers ? curve.others.filter(s => !hidden[s.key]) : [])].filter(s => s.points.length) : []
	const labelName = s => (s.role === 'you' ? `${s.name} · You` : s.name)
	const columnWidth = endLabels ? labelColumnWidth(drawnLines.map(labelName)) : 0

	useLayoutEffect(() => {
		const root = am5.Root.new(el.current)
		root._logo?.dispose()
		const chart = root.container.children.push(am5xy.XYChart.new(root, {
			panX: false, panY: false, wheelX: 'none', wheelY: 'none', paddingLeft: 0, paddingRight: 28, paddingTop: 8, paddingBottom: 0,
		}))
		const xRenderer = am5xy.AxisRendererX.new(root, { inversed: true, minGridDistance: 45 })
		// the 0 of the axis reads "pick-up" (a range label keeps its own text)
		xRenderer.labels.template.adapters.add('text', text => (text === '0' ? 'pick-up' : text))
		const xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, { min: 0, max: curve.days, strictMinMax: true, maxPrecision: 0, renderer: xRenderer }))
		xAxis.children.push(am5.Label.new(root, { text: 'Days before pick-up', x: am5.p50, centerX: am5.p50, fontSize: 11, fill: am5.color(dsColorNew.textMuted) }))
		const yRenderer = am5xy.AxisRendererY.new(root, {})
		yRenderer.labels.template.adapters.add('text', (text, target) => {
			const v = target.dataItem?.get('value')
			return v === undefined || v === null ? text : money(v, code)
		})
		// prices are never negative: the axis starts at a round value under the lowest price drawn, never below 0
		const drawnValues = [...curve.main.filter(s => !hidden[s.key]), ...(showOthers ? curve.others.filter(s => !hidden[s.key]) : [])]
			.flatMap(s => [...s.points, ...(showPrev && s.prev ? s.prev : [])].map(p => p.value))
		const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, { renderer: yRenderer, extraMax: 0.05, min: axisFloor(drawnValues) }))

		if (zone) {
			const range = xAxis.createAxisRange(xAxis.makeDataItem({ value: zone.to, endValue: zone.from }))
			range.get('axisFill').setAll({ visible: true, fill: am5.color('#EEF1F5'), fillOpacity: 0.8 })
			range.get('grid').set('forceHidden', true)
			// at the top of the plot (QA): at the bottom it sat on the lowest line
			range.get('label').setAll({ text: zone.label, inside: true, centerY: am5.p0, fontSize: 11, fill: am5.color(dsColorNew.textMuted), location: 0.5 })
			range.get('label').adapters.add('dy', () => -chart.plotContainer.height() + 4)
		}
		if (marker) {
			const range = xAxis.createAxisRange(xAxis.makeDataItem({ value: marker.d }))
			const color = am5.color(marker.color || dsColorNew.noteInfo)
			range.get('grid').setAll({ stroke: color, strokeOpacity: marker.color ? 0.6 : 1, strokeDasharray: [3, 3], forceHidden: false })
			// the cell's marks sit left of their line; the date curve's "moved first" (with its colour) right of it, as the mock
			range.get('label').setAll(marker.color
				? { text: '' } // drawn at the top of the plot by the overlay (`ends.mark`)
				: { text: marker.label, inside: true, centerY: am5.p100, centerX: am5.p100, dy: -8, dx: -4, fontSize: 11, fill: color })
		}

		const dim = s => !!highlight && s.key !== highlight && s.role !== 'you' && s.role !== 'median'
		const add = (s, points, dashed) => {
			const series = chart.series.push(am5xy.LineSeries.new(root, {
				xAxis, yAxis, valueXField: 'd', valueYField: 'value', connect: true, stroke: am5.color(s.color), fill: am5.color(s.color),
			}))
			series.strokes.template.setAll({ strokeWidth: s.key === highlight ? s.width + 1 : s.width, strokeOpacity: dim(s) ? 0.16 : 1, ...(dashed && { strokeDasharray: [4, 4] }) })
			if (allBullets || s.role !== 'other') {
				series.bullets.push((r, _series, dataItem) => {
					const thin = !!dataItem.dataContext?.thin
					return am5.Bullet.new(r, { sprite: am5.Circle.new(r, {
						radius: thin ? 3.5 : 2.5, strokeWidth: 1.5, stroke: am5.color(s.color), fill: am5.color(thin ? dsColorNew.white : s.color),
						opacity: dim(s) ? 0.16 : 1,
					}) })
				})
			}
			const sorted = [...points].sort((a, b) => a.d - b.d)
			if (ring && ring.key === s.key && !dashed) {
				series.bullets.push((r, _series, dataItem) => (dataItem.dataContext?.d === ring.d
					? am5.Bullet.new(r, { sprite: am5.Circle.new(r, { radius: 8, strokeWidth: 2, stroke: am5.color(dsColorNew.textTitle), fillOpacity: 0 }) })
					: undefined))
			}
			series.data.setAll(sorted)
		}
		// line-end labels (CA 7, as the mock): in a column right of the plot, placed once the axes have their real range
		if (endLabels) {
			let seen = null
			root.events.on('frameended', () => {
				const pr = chart.plotContainer
				const key = `${yAxis.getPrivate('min')}|${yAxis.getPrivate('max')}|${xAxis.getPrivate('min')}|${pr.width()}|${pr.height()}`
				if (key === seen) return
				seen = key
				const at = (d, v) => pr.toGlobal({ x: xRenderer.positionToCoordinate(xAxis.valueToPosition(d)), y: yRenderer.positionToCoordinate(yAxis.valueToPosition(v)) })
				const items = drawnLines.map((s, i) => {
					const last = [...s.points].sort((a, b) => a.d - b.d)[0]
					const g = at(last.d, last.value)
					const priority = s.role === 'you' ? 0 : s.role === 'median' ? 1 : s.key === highlight ? 2 : i + 10
					return { key: s.key, name: labelName(s), color: s.color, bold: s.role !== 'other', dim: dim(s), x: g.x, y: g.y, priority }
				})
				const top = pr.toGlobal({ x: 0, y: 0 }), bottom = pr.toGlobal({ x: pr.width(), y: pr.height() })
				const layout = labelLayout({ ends: items, plotRight: bottom.x, top: top.y + 6, bottom: bottom.y - 6 })
				const byKey = new Map(items.map(i => [i.key, i]))
				const mark = marker?.color ? { x: at(marker.d, yAxis.getPrivate('max') ?? 0).x, y: top.y, label: marker.label, color: marker.color } : null
				setEnds({ ...layout, labels: layout.labels.map(l => ({ ...l, ...byKey.get(l.key), y: l.y })), mark, right: bottom.x })
			})
		}
		if (showOthers) curve.others.filter(s => !hidden[s.key]).forEach(s => add(s, s.points, false))
		curve.main.filter(s => !hidden[s.key]).forEach(s => {
			if (showPrev && s.prev?.length) add(s, s.prev, true)
			add(s, s.points, false)
		})

		const cursor = chart.set('cursor', am5xy.XYCursor.new(root, { behavior: 'none', xAxis }))
		cursor.lineY.set('visible', false)
		const dayAt = () => {
			const position = cursor.getPrivate('positionX')
			if (position === undefined || position === null) return null
			return Math.max(0, Math.min(curve.days, Math.round(xAxis.positionToValue(xAxis.toAxisPosition(position)))))
		}
		const xOf = d => {
			const px = xRenderer.positionToCoordinate(xAxis.valueToPosition(d))
			return chart.plotContainer.toGlobal({ x: px, y: 0 }).x
		}
		cursor.events.on('cursormoved', () => {
			const d = dayAt()
			if (d !== null) onHover({ d, x: xOf(d) })
		})
		cursor.events.on('cursorhidden', () => onHover(null))
		if (onDayClick) {
			chart.plotContainer.events.on('click', () => {
				const d = dayAt()
				if (d !== null) onDayClick(d)
			})
			chart.plotContainer.set('cursorOverStyle', 'pointer')
		}

		api.current = {
			moveTo: d => {
				// amCharts 5.4: `handleMove` takes a global point (no `triggerMove` yet)
				const px = xRenderer.positionToCoordinate(xAxis.valueToPosition(d))
				cursor.handleMove(chart.plotContainer.toGlobal({ x: px, y: chart.plotContainer.height() / 2 }))
				onHover({ d, x: xOf(d) })
			},
		}
		return () => {
			api.current = null
			root.dispose()
		}
	}, [curve, hidden, showPrev, showOthers, code, marker?.d, marker?.color, zone?.from, highlight, endLabels, ring?.key, ring?.d, height])

	const onKeyDown = e => {
		if (e.key === 'Enter' && onDayClick && hoverRef.current?.d !== undefined && hoverRef.current?.d !== null) {
			e.preventDefault()
			onDayClick(hoverRef.current.d)
			return
		}
		if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return
		e.preventDefault()
		api.current?.moveTo(nextCursorDay(hover?.d ?? null, e.key, curve.days))
	}

	const chartBox = <Box ref={el} tabIndex={0} role="img" aria-label={label} onKeyDown={onKeyDown}
		sx={{ height, width: '100%', minWidth: 0, '&:focus-visible': { outline: `2px solid ${dsColorNew.webBlue500}`, outlineOffset: 2, borderRadius: '6px' } }}/>
	if (!endLabels) return chartBox
	return <Box sx={{ position: 'relative', display: 'flex', height }}>
		<Box sx={{ flex: 1, minWidth: 0 }}>{chartBox}</Box>
		<Box aria-hidden="true" sx={{ width: columnWidth, flexShrink: 0 }}/>
		{!!ends && <Box component="svg" aria-hidden="true" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', overflow: 'visible' }}>
			{!!ends.mark && <text x={ends.mark.x > ends.right - 160 ? ends.mark.x - 6 : ends.mark.x + 6} y={ends.mark.y + 12} textAnchor={ends.mark.x > ends.right - 160 ? 'end' : 'start'}
				fontSize={11} fontWeight={600} fill={ends.mark.color} fontFamily="Roboto, sans-serif">{ends.mark.label}</text>}
			{ends.labels.map(l => <g key={l.key} opacity={l.dim ? 0.3 : 1}>
				<line x1={l.fromX + 3} y1={l.fromY} x2={ends.x - 2} y2={l.y} stroke={l.color} strokeWidth={0.8} opacity={0.45}/>
				<text x={ends.x} y={l.y + 4} fontSize={11} fontWeight={l.bold ? 700 : 500} fill={l.color} fontFamily="Roboto, sans-serif">{l.name}</text>
			</g>)}
		</Box>}
	</Box>
}

export function Tooltip({ tip, x, code, footer = null }) {
	return <Box sx={{
		position: 'absolute', top: 4, left: Math.max(4, x + 12), transform: x > 420 ? 'translateX(calc(-100% - 24px))' : 'none', pointerEvents: 'none',
		backgroundColor: dsColorNew.white, border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '10px', boxShadow: '0 12px 32px rgba(12,26,62,.12)',
		p: '8px 10px', fontSize: 12, color: dsColorNew.textDark, minWidth: 190, zIndex: 3,
	}}>
		<Box sx={{ fontWeight: 600, color: dsColorNew.textTitle }}>{tip.title}</Box>
		{!!tip.captured && <Box sx={{ color: dsColorNew.textMuted, mb: 0.5 }}>{tip.captured}</Box>}
		{tip.rows.map(r => <Box key={r.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5, alignItems: 'center' }}>
			<Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
				<Box component="i" sx={{ width: 10, height: 3, borderRadius: '2px', backgroundColor: r.color, display: 'inline-block' }}/>{r.name}{r.tag ? ` · ${r.tag}` : ''}
			</Box>
			<Box component="span" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
				{r.value === null ? <Box component="span" sx={{ color: dsColorNew.textSubtle }}>No prices that day</Box> : money(r.value, code)}
				{r.thin && <Box component="span" sx={{ color: dsColorNew.noteAmber, fontWeight: 700, ml: 0.5 }}>!</Box>}
				{r.ly !== null && <Box component="span" sx={{ color: dsColorNew.textMuted, ml: 1 }}>LY {money(r.ly, code)}</Box>}
			</Box>
		</Box>)}
		{!!footer && <Box sx={{ color: dsColorNew.textMuted, mt: 0.5, pt: 0.5, borderTop: `1px solid ${dsColorNew.grayBorder}` }}>{footer}</Box>}
	</Box>
}

export const tipText = (tip, code) => [tip.title, tip.captured, ...tip.rows.map(r => `${r.name}: ${r.value === null ? 'no prices that day' : money(r.value, code)}${r.thin ? ', few prices' : ''}${r.ly !== null ? `, last year ${money(r.ly, code)}` : ''}`)].filter(Boolean).join('. ')

/**
 * "Price curve before pick-up" inside the detail of a cell (CU-869fmhmx8 E9, UX v4): You, the provider opened and the
 * market median of the providers shown; last year's curve (dotted) and the other competitors as switches, off when
 * opening. Loads on its own: the rest of the detail is already painted with the table's data.
 */
export default function PriceCurve({ curve, loading, error, onRetry, stat, lor, code, today, onExplorer }) {
	const [hidden, setHidden] = useState({})
	const [showPrev, setShowPrev] = useState(false)
	const [showOthers, setShowOthers] = useState(false)
	const [hover, setHover] = useState(null)

	const marker = useMemo(() => (curve ? compareMarker({ compare: curve.compare, pickupDates: curve.pickupDates, days: curve.days, today }) : null), [curve, today])
	const zone = useMemo(() => (curve ? notCapturedZone({ pickupDates: curve.pickupDates, days: curve.days, today }) : null), [curve, today])
	const tip = curve && hover ? curveTooltip(curve, hover.d, { hidden, showPrev, showOthers }) : null

	const header = <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
		<Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
			<Typography sx={{ fontSize: 14, fontWeight: 600, color: dsColorNew.textTitle }}>Price curve before pick-up</Typography>
			{!!curve && <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 0.25 }}>{curveSubtitle({ pickupDates: curve.pickupDates, stat, lor })}</Typography>}
		</Box>
		<Button variant="text" size="small" onClick={onExplorer} startIcon={<IconSeeker icon="sliders" sx={{ fontSize: 12 }}/>} sx={{ textTransform: 'none' }}>
			All providers in {BI_ROUTES.TITLE}
		</Button>
	</Box>

	if (loading) return <Box component="section" aria-label="Price curve" sx={box}>
		{header}
		<Skeleton variant="rounded" width="100%" height={HEIGHT} aria-busy="true" sx={{ mt: 1.25, borderRadius: '10px', transform: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', visibility: 'visible' }}>
			<Typography sx={{ visibility: 'visible', fontSize: 13, color: dsColorNew.textMuted }}>Loading the price curve…</Typography>
		</Skeleton>
	</Box>

	if (error) return <Box component="section" aria-label="Price curve" sx={box}>
		{header}
		<AppNote severity="error" sx={{ mt: 1.25 }}>
			<b>Could not load the price curve.</b> The rest of this window is up to date.
			<Box sx={{ mt: 1 }}><Button variant="outlined" onClick={onRetry} sx={secondaryButtonSx}>Try again</Button></Box>
		</AppNote>
	</Box>

	if (!curve || curve.empty) return <Box component="section" aria-label="Price curve" sx={box}>
		{header}
		<Box sx={{ py: 3 }}><AppEmptyLine>{noCurveText(curve?.days || 45)}</AppEmptyLine></Box>
	</Box>

	const visibleMain = curve.main.filter(s => !hidden[s.key])
	const foot = curveFoot(curve, { hidden, showPrev })
	const label = `Price curve: ${visibleMain.map(s => s.name).join(', ')}, by days before pick-up. Use the left and right arrows to move day by day.`

	return <Box component="section" aria-label="Price curve" sx={box}>
		{header}
		<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center', mt: 1.25, mb: 0.5 }}>
			{curve.main.map(s => <ButtonBase key={s.key} aria-pressed={!hidden[s.key]} onClick={() => setHidden(h => ({ ...h, [s.key]: !h[s.key] }))}
				sx={{
					display: 'inline-flex', alignItems: 'center', gap: 0.75, height: 26, px: 1.1, borderRadius: '8px', border: `1px solid ${dsColorNew.grayBorder}`,
					fontSize: 12.5, color: hidden[s.key] ? dsColorNew.textSubtle : dsColorNew.textDark, backgroundColor: hidden[s.key] ? dsColorNew.pillTrackBg : dsColorNew.white,
					'&.Mui-focusVisible': { outline: `2px solid ${dsColorNew.webBlue500}` },
				}}>
				<Box component="i" sx={{ width: 14, height: 3, borderRadius: '2px', display: 'inline-block', backgroundColor: s.color, opacity: hidden[s.key] ? 0.35 : 1 }}/>
				{s.providerId !== undefined && <AppBrandMark name={s.name} size={14}/>}
				{s.name}
				{!!s.tag && <Box component="span" sx={{ px: 0.75, borderRadius: '999px', fontSize: 11, fontWeight: 600, backgroundColor: dsColorNew.pillActiveBg, color: dsColorNew.appBlue }}>{s.tag}</Box>}
			</ButtonBase>)}
			<Box sx={{ flex: 1 }}/>
			{curve.hasPrev && <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
				<AppAntSwitch checked={showPrev} onChange={() => setShowPrev(v => !v)} inputProps={{ role: 'switch', 'aria-label': 'Last year\'s curve (dotted)' }}/>
				<Typography sx={{ fontSize: 12.5, color: dsColorNew.textDark, cursor: 'pointer' }} onClick={() => setShowPrev(v => !v)}>Last year's curve <Box component="span" sx={{ color: dsColorNew.textSubtle }}>(dotted)</Box></Typography>
			</Box>}
			{!!curve.others.length && <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
				<AppAntSwitch checked={showOthers} onChange={() => setShowOthers(v => !v)} inputProps={{ role: 'switch', 'aria-label': `Other competitors (${curve.others.length})` }}/>
				<Typography sx={{ fontSize: 12.5, color: dsColorNew.textDark, cursor: 'pointer' }} onClick={() => setShowOthers(v => !v)}>Other competitors ({curve.others.length})</Typography>
			</Box>}
		</Box>

		{!visibleMain.length && !showOthers
			? <Box sx={{ py: 3 }}><AppEmptyLine>Every line is hidden. Click a name above to show it.</AppEmptyLine></Box>
			: <Box sx={{ position: 'relative' }} onMouseLeave={() => setHover(null)}>
				<CurveChart curve={curve} hidden={hidden} showPrev={showPrev} showOthers={showOthers} code={code} marker={marker} zone={zone}
					hover={hover} onHover={setHover} label={label}/>
				{!!tip && <Tooltip tip={tip} x={hover.x} code={code}/>}
				<Box aria-live="polite" sx={visuallyHidden}>{tip ? tipText(tip, code) : ''}</Box>
			</Box>}

		<Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', fontSize: 12, color: dsColorNew.textMuted, mt: 0.5 }}>
			{!!foot.thin && <span>{foot.thin}</span>}
			{!!foot.sparse && <span>{foot.sparse}</span>}
			<span>{foot.gaps}</span>
		</Box>
	</Box>
}
